import { useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { validateCalendarEventFeedbackQuestion } from 'shared-helpers/calendar';
import { isEmptyBlockContent } from 'shared-helpers/common';
import { Button, ModalButtonWithForm } from 'components/common';
import {
  ArrayField,
  Checkbox,
  DisplayField,
  FormGroup,
  NumberStepper,
  RichText,
  Select,
  Text
} from 'components/common/form';
import { CalendarEventFeedbackQuestionSelectionLimits } from '.';

import type { FormApi } from 'informed';
import type { CalendarEventFeedbackQuestion } from 'shared-types';

type QuestionType = CalendarEventFeedbackQuestion['type'];

type FormValues = {
  isRequired: boolean;
  numberOfAllowedSelectionsMax: number;
  numberOfAllowedSelectionsMin: number;
  numberOfStars: number;
  options: { id: string; text: string }[];
  question: string;
  type: QuestionType;
};

interface Props {
  disabled?: boolean;
  isInsideDropdown?: boolean;
  question?: CalendarEventFeedbackQuestion;
  questionsUrl: string;
  updateQuestions: () => Promise<void>;
}

const CalendarEventFeedbackQuestionSave = ({
  disabled = false,
  isInsideDropdown = false,
  question,
  questionsUrl,
  updateQuestions
}: Props) => {
  const { dispatch } = useDispatch();

  const formApi = useRef<FormApi<FormValues>>(null);

  const { messages } = useReduxState();

  const typeOptions = [
    {
      label: messages.eventCalendarFeedbackTypeOpenQuestion,
      value: 'openquestion'
    },
    {
      label: messages.eventCalendarFeedbackTypeSingleChoice,
      value: 'singlechoice'
    },
    {
      label: messages.eventCalendarFeedbackTypeMultipleChoice,
      value: 'multiplechoice'
    },
    { label: messages.eventCalendarFeedbackTypeStarRating, value: 'starrating' }
  ];

  const isChoiceQuestion =
    question?.type === 'singlechoice' || question?.type === 'multiplechoice';

  // a choice question needs at least one option, so the list
  // starts with an empty one and the last one cannot be removed
  // - that keeps the invalid state out of reach
  const seedOptions = useMemo(() => [{ id: uuidv4(), text: '' }], []);

  const validateText = (value: string) =>
    !value?.trim() ? messages.commonFieldRequired : undefined;

  // the question is rich text, so an empty editor still holds markup like
  // "<p></p>" and only counts as blank once the tags are stripped
  const validateQuestion = (value: string) =>
    isEmptyBlockContent(value) ? messages.commonFieldRequired : undefined;

  const initialValues: FormValues = {
    isRequired: question?.isRequired || false,
    numberOfAllowedSelectionsMax: isChoiceQuestion
      ? question.data.numberOfAllowedSelectionsMax
      : 0,
    numberOfAllowedSelectionsMin: isChoiceQuestion
      ? question.data.numberOfAllowedSelectionsMin
      : 0,
    numberOfStars:
      question?.type === 'starrating' ? question.data.numberOfStars : 5,
    options: isChoiceQuestion ? question.data.options : seedOptions,
    question: question?.question || '',
    type: question?.type || 'openquestion'
  };

  const getQuestionData = (values: FormValues) => {
    if (values.type === 'singlechoice') {
      // a single choice question allows exactly one selection, so its limits
      // are derived rather than set by the user
      return {
        numberOfAllowedSelectionsMax: 1,
        numberOfAllowedSelectionsMin: values.isRequired ? 1 : 0,
        options: values.options || []
      };
    }

    if (values.type === 'multiplechoice') {
      return {
        numberOfAllowedSelectionsMax: values.numberOfAllowedSelectionsMax,
        numberOfAllowedSelectionsMin: values.numberOfAllowedSelectionsMin,
        options: values.options || []
      };
    }

    if (values.type === 'starrating') {
      return { numberOfStars: values.numberOfStars || 5 };
    }

    return null;
  };

  const validateBeforeSubmit = (values: FormValues) => {
    const validationError = validateCalendarEventFeedbackQuestion({
      data: getQuestionData(values),
      isRequired: values.isRequired,
      question: values.question,
      type: values.type
    });
    if (!validationError) return true;

    // the modal submits through a plain button, so informed never runs the
    // field validators on its own - without this the button would just go
    // disabled and leave the offending field unmarked
    formApi.current?.validate();

    return false;
  };

  const handleSubmit = async (values: FormValues) => {
    await api.post(questionsUrl, {
      data: getQuestionData(values),
      isRequired: values.isRequired,
      question: values.question,
      type: values.type,
      ...(question ? { questionId: question.id } : {})
    });

    await updateQuestions();

    dispatch.addNotice({
      text: messages.eventCalendarFeedbackQuestionSaveSuccess,
      type: 'success'
    });
  };

  return (
    <ModalButtonWithForm
      {...(isInsideDropdown
        ? {
            buttonIcon: 'pen',
            buttonIsDropdown: true,
            buttonText: messages.commonEdit
          }
        : {
            buttonCategory: 'success',
            buttonIcon: 'plus',
            buttonSize: 'header',
            buttonText: messages.eventCalendarFeedbackAddQuestion
          })}
      apiRef={formApi}
      buttonDisabled={disabled}
      buttonProps={
        disabled
          ? { tooltip: messages.eventCalendarFeedbackQuestionsLockedTooltip }
          : undefined
      }
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={
        question ? messages.commonSaveChanges : messages.commonAdd
      }
      modalTitle={
        question
          ? messages.eventCalendarFeedbackEditQuestion
          : messages.eventCalendarFeedbackAddQuestion
      }
      modalWidth="medium"
      onSubmit={handleSubmit}
      preventSubmit={true}
      validateBeforeSubmit={validateBeforeSubmit}
    >
      {({ values }: { values: FormValues }) => (
        <>
          <FormGroup
            htmlFor="feedback-question-text"
            label={messages.commonQuestion}
          >
            <RichText
              field="question"
              id="feedback-question-text"
              maxLength={2000}
              validate={validateQuestion}
              validateOnChange={true}
            />
          </FormGroup>

          <FormGroup
            htmlFor="feedback-question-type"
            label={messages.commonType}
          >
            <Select
              field="type"
              id="feedback-question-type"
              isClearable={false}
              options={typeOptions}
              style={{ maxWidth: 300 }}
            />
          </FormGroup>

          <Checkbox
            field="isRequired"
            id="feedback-question-required"
            label={messages.commonRequired}
            style={{ marginTop: 20 }}
          />

          {values?.type === 'singlechoice' ||
          values?.type === 'multiplechoice' ? (
            <FormGroup label={messages.commonOptions}>
              <ArrayField field="options">
                {({ addWithInitialValue, swap, fields }) => (
                  <div className="options">
                    <ArrayField.Items>
                      {({ field, index, remove }) => (
                        <div className="option">
                          <DisplayField field={`${field}.id`} />

                          <div className="option-text">
                            <Text
                              field={`${field}.text`}
                              id={`feedback-question-option-${index}`}
                              required={true}
                              validate={validateText}
                            />
                          </div>

                          <div className="option-actions">
                            <Button
                              category="light"
                              disabled={index === 0}
                              icon="arrow-up"
                              onClick={() => swap(index, index - 1)}
                              size="small"
                              tooltip={messages.commonMoveUp}
                            />

                            <Button
                              category="light"
                              disabled={index === fields.length - 1}
                              icon="arrow-down"
                              onClick={() => swap(index, index + 1)}
                              size="small"
                              tooltip={messages.commonMoveDown}
                            />

                            <Button
                              category="light"
                              disabled={fields.length === 1}
                              icon="trash-alt"
                              onClick={remove}
                              size="small"
                              tooltip={messages.commonRemove}
                            />
                          </div>
                        </div>
                      )}
                    </ArrayField.Items>

                    <Button
                      category="lighter"
                      icon="plus"
                      onClick={() =>
                        addWithInitialValue({ id: uuidv4(), text: '' })
                      }
                      size="small"
                      text={messages.commonAddOption}
                    />

                    <style jsx>{`
                      .options {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 10px;
                      }
                      .option {
                        align-items: flex-start;
                        display: flex;
                        gap: 10px;
                        width: 100%;
                      }
                      .option-text {
                        flex-grow: 1;
                      }
                      .option-actions {
                        align-items: flex-start;
                        display: flex;
                        gap: 5px;
                        padding-top: 4px;
                      }
                    `}</style>
                  </div>
                )}
              </ArrayField>
            </FormGroup>
          ) : null}

          {values?.type === 'multiplechoice' ? (
            <CalendarEventFeedbackQuestionSelectionLimits
              // only multiple choice questions carries limits the user chooses
              hasSavedLimits={question?.type === 'multiplechoice'}
              isRequired={values.isRequired}
              numberOfOptions={values.options?.length || 0}
            />
          ) : null}

          {values?.type === 'starrating' ? (
            <FormGroup
              htmlFor="feedback-question-number-of-stars"
              label={messages.eventCalendarFeedbackNumberOfStars}
            >
              <NumberStepper
                field="numberOfStars"
                id="feedback-question-number-of-stars"
                max={10}
                min={1}
                stepValue={1}
              />
            </FormGroup>
          ) : null}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default CalendarEventFeedbackQuestionSave;
